import { PolfEventRankingEnum } from '@polf/shared/codegen/types'

import type { LeaderboardUnionFieldsFragment } from '../../__generated__/hooks'
import BestBallView from './BestBallView'
import GolfView from './GolfView'
import type { CourseHoleSummary } from './MiniScorecard'
import PolfView from './PolfView'
import ScrambleView from './ScrambleView'

/**
 * Leaderboard dispatcher. Narrows the `LeaderboardUnion` once and forwards
 * to the per-variant view. Each variant owns its own state (toggles,
 * derived hero data) so this file stays small and the variants stay
 * independently navigable.
 *
 * Public spectator surface (polf-event-key auth) does NOT live in this
 * package — it belongs to `packages/projector`. This component is
 * player-scoped: it takes a `youId` (always set) and an optional
 * `yourTeamId` for team formats.
 *
 * `courseHoles` is the canonical list of holes for the event — drilled
 * through to `MiniScorecard` so the detail sheet can render all holes
 * (not just the scored ones). The leaderboard resolver only ships
 * `PolfEventScore` rows for registered scores, so the consumer needs
 * the course hole list separately.
 */
type Props = {
  leaderboard: LeaderboardUnionFieldsFragment | null
  /** Authenticated player id — drives "Du" highlight + hero. */
  youId?: string
  /** Authenticated player's team id; drives the team hero on best-ball/scramble. */
  yourTeamId?: string | null
  /** Event ranking rule (Golf/Scramble/BestBall). */
  ranking?: PolfEventRankingEnum | null
  /** All course holes — drilled through to MiniScorecard in the detail sheet. */
  courseHoles: readonly CourseHoleSummary[]
}

const LeaderboardView = ({
  leaderboard: lb,
  youId,
  yourTeamId,
  ranking,
  courseHoles,
}: Props) => {
  if (!lb) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Ingen leaderboard ennå.
      </section>
    )
  }
  switch (lb.__typename) {
    case 'PolfLeaderboard':
      return <PolfView lb={lb} youId={youId} courseHoles={courseHoles} />
    case 'GolfLeaderboard':
      return (
        <GolfView
          lb={lb}
          youId={youId}
          ranking={ranking}
          courseHoles={courseHoles}
        />
      )
    case 'ScrambleLeaderboard':
      return (
        <ScrambleView
          lb={lb}
          yourTeamId={yourTeamId}
          ranking={ranking}
          courseHoles={courseHoles}
        />
      )
    case 'BestBallLeaderboard':
      return (
        <BestBallView
          lb={lb}
          youId={youId}
          yourTeamId={yourTeamId}
          ranking={ranking}
          courseHoles={courseHoles}
        />
      )
    default:
      return (
        <section className="px-4 py-8 text-center text-ink-500">
          Ukjent leaderboard-format.
        </section>
      )
  }
}

export default LeaderboardView
